/**
 * Color utilities for minister status/inactive reason coding.
 * All colors use CSS custom properties so they adapt to light/dark themes.
 */

import type { BadgeProps } from "@/components/ui/badge";

type BadgeVariant = BadgeProps["variant"];

/**
 * Map inactive_reason_code to badge variant.
 * Active minister flagged as missing from credentials uses "default" (primary/turquoise).
 */
export function getInactiveReasonBadgeVariant(code: string | null): BadgeVariant {
  if (!code) return "outline"; // active, no flag
  const map: Record<string, BadgeVariant> = {
    retired:              "muted",
    passive:              "accent",
    abandon:              "warning",
    deceased:             "outline",    // special: dark foreground tint handled via row bg
    observed:             "secondary",
    discharged:           "rose",
    dismissed:            "destructive",
    disciplinary_process: "info",
  };
  return map[code] ?? "outline";
}

/**
 * Row background style for a minister entry.
 * Returns an inline style object using CSS var tints — works in both themes.
 * Pass missingFromCredentials=true for active ministers not in any active request.
 */
export function getMinisterRowStyle(
  isActive: boolean,
  inactiveReasonCode: string | null,
  missingFromCredentials: boolean
): React.CSSProperties {
  if (!isActive) {
    return getInactiveRowStyle(inactiveReasonCode);
  }
  if (missingFromCredentials) {
    return { backgroundColor: "hsl(var(--primary) / 0.08)" };
  }
  return {};
}

function getInactiveRowStyle(code: string | null): React.CSSProperties {
  if (!code) return {};
  const map: Record<string, React.CSSProperties> = {
    retired:              { backgroundColor: "hsl(var(--muted))" },
    passive:              { backgroundColor: "hsl(var(--accent) / 0.08)" },
    abandon:              { backgroundColor: "hsl(var(--warning) / 0.08)" },
    deceased:             { backgroundColor: "hsl(var(--foreground) / 0.06)" },
    observed:             { backgroundColor: "hsl(var(--secondary) / 0.5)" },
    discharged:           { backgroundColor: "hsl(var(--rose) / 0.08)" },
    dismissed:            { backgroundColor: "hsl(var(--destructive) / 0.08)" },
    disciplinary_process: { backgroundColor: "hsl(var(--info) / 0.08)" },
  };
  return map[code] ?? {};
}

/**
 * Human-readable label for missing_from_credentials badge.
 */
export const MISSING_CREDENTIALS_LABEL = "Sin credencial";

/**
 * Translate inactive_reason_code to a Spanish label fallback
 * (in case the DB label is not available).
 */
export function translateInactiveReason(code: string | null): string {
  if (!code) return "Activo";
  const map: Record<string, string> = {
    retired:              "Retirado",
    passive:              "Pasivo",
    abandon:              "Abandono",
    deceased:             "Fallecido",
    observed:             "Observado",
    discharged:           "Dado de baja",
    dismissed:            "Destituido",
    disciplinary_process: "Proceso disciplinario",
  };
  return map[code] ?? code;
}
